import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { parseLogoUrl, getFallbackLogo } from '../utils/logoUtils';

/**
 * TENANT CONTEXT PROVIDER
 *
 * Purpose: Provide tenant configuration globally with dynamic theme injection
 * Features:
 * - Automatic tenant resolution from hostname (production-ready)
 * - CSS variable injection for branding
 * - Full-page loading and error states
 */

const TenantContext = createContext(null);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.theskillgenie.com';

/**
 * Tenant Provider Component
 */
export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const applyBranding = useCallback((tenantData) => {
    const root = document.documentElement;
    
    const primary = tenantData.primaryColor || 'var(--primary-blue, #4F46E5)';
    const secondary = tenantData.secondaryColor || '#8B5CF6';
    
    root.style.setProperty('--brand-primary', primary);
    root.style.setProperty('--brand-secondary', secondary);
    root.style.setProperty('--brand-accent', tenantData.accentColor || '#10B981');
    root.style.setProperty('--bg-header-footer', primary);
    
    // Also update theme-color meta tag if exists
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', primary);
    }
  }, []);

  const updatePageTitle = useCallback((tenantData) => {
    const hostname = window.location.hostname;
    const isDefault = hostname === 'theskillgenie.com' || hostname === 'www.theskillgenie.com' || hostname.includes('localhost');
    
    console.log('Updating page title for tenant:', tenantData.collegeName);
    if (isDefault) {
      document.title = 'TheSkillGenie';
    } else {
      document.title = `${tenantData.collegeName} | TheSkillGenie`;
    }
  }, []);

  const resolveTenant = useCallback(async () => {
    const hostname = window.location.hostname;
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tenants/resolve`, {
        params: { host: hostname },
        timeout: 8000,
      });

      const tenantData = response.data;
      
      // Only update state if data actually changed to prevent unnecessary re-renders
      setTenant(prev => {
        if (JSON.stringify(prev) === JSON.stringify(tenantData)) return prev;
        return tenantData;
      });

      // Apply CSS variables for dynamic branding
      applyBranding(tenantData);

      // Update page title
      updatePageTitle(tenantData);

    } catch (err) {
      console.error('Failed to resolve tenant:', err);

      const status = err.response?.status;
      const baseDomain = process.env.REACT_APP_BASE_DOMAIN || 'theskillgenie.com';
      const isTenantHost =
        hostname !== baseDomain &&
        hostname !== `www.${baseDomain}` &&
        hostname !== 'localhost' &&
        !hostname.startsWith('127.') &&
        hostname.endsWith(`.${baseDomain}`);

      if (status === 404) {
        // Only treat as fatal on a real tenant subdomain; main-domain 404 is fine
        if (isTenantHost) {
          setError('TENANT_NOT_FOUND');
        }
      } else if (status === 403) {
        setError('TENANT_INACTIVE');
      } else {
        // Network / 500 errors - only block the UI on tenant subdomains
        if (isTenantHost) {
          setError('RESOLUTION_FAILED');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [applyBranding, updatePageTitle]);

  useEffect(() => {
    resolveTenant();
  }, [resolveTenant]);

  const isWhiteLabelEnabled = () => tenant?.whiteLabelEnabled || false;
  const getCollegeName = () => tenant?.collegeName || 'The Skill Genie';
  const getLogoUrl = () => {
    const logo = parseLogoUrl(tenant?.logoUrl);
    return logo || getFallbackLogo();
  };
  const getPrimaryColor = () => tenant?.primaryColor || 'var(--primary-blue, #4F46E5)';
  const getSecondaryColor = () => tenant?.secondaryColor || '#8B5CF6';

  // College contact detail getters - backed by the resolve API (colleges JOIN).
  // Returns null when a field is absent so callers can hide the element cleanly.
  const getCollegePhone   = () => tenant?.collegePhone   || null;
  const getCollegeEmail   = () => tenant?.collegeEmail   || null;
  const getCollegeWebsite = () => tenant?.collegeWebsite || null;
  /** Returns "State, Country", "State", "Country", or null */
  const getCollegeLocation = () => {
    const parts = [tenant?.collegeState, tenant?.collegeCountry].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const isFeatureEnabled = (flag) => {
    if (!tenant?.featureFlags) return true; // Default to enabled if no flags defined
    return tenant.featureFlags[flag] !== false;
  };

  /** Returns a custom college description or null */
  const getCollegeDescription = () => tenant?.collegeDescription || null;

  /** Returns full address string or null — combines street, city, state, country */
  const getCollegeAddress = () => {
    const parts = [
      tenant?.collegeAddress,
      tenant?.collegeCity,
      tenant?.collegeState,
      tenant?.collegeCountry,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  /**
   * Returns an object with social media URLs.
   * Each key is null when the URL is not configured.
   * Reads from top-level tenant fields OR tenant.socialLinks object (whichever is present).
   */
  const getSocialLinks = () => {
    const sl = tenant?.socialLinks || {};
    return {
      linkedin:  tenant?.linkedinUrl  || sl.linkedin  || null,
      youtube:   tenant?.youtubeUrl   || sl.youtube   || null,
      github:    tenant?.githubUrl    || sl.github    || null,
      instagram: tenant?.instagramUrl || sl.instagram || null,
    };
  };

  const value = {
    tenant,
    loading,
    error,
    isWhiteLabelEnabled,
    getCollegeName,
    getLogoUrl,
    getPrimaryColor,
    getSecondaryColor,
    getCollegePhone,
    getCollegeEmail,
    getCollegeWebsite,
    getCollegeLocation,
    getCollegeDescription,
    getCollegeAddress,
    getSocialLinks,
    isFeatureEnabled,
    resolveTenant
  };

  if (loading) {
    return (
      <div style={{ 
        height: '100vh', 
        width: '100vw', 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center',
        background: '#f8fafc' 
      }}>
        <div className="animate-pulse" style={{ color: '#64748b', fontSize: '1.1rem' }}>Loading platform...</div>
      </div>
    );
  }

  if (error === 'TENANT_NOT_FOUND') {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '10px' }}>Institution Not Found</h1>
        <p style={{ color: '#64748b', marginBottom: '20px' }}>
          The subdomain <strong>{window.location.hostname}</strong> is not registered on our platform.
        </p>
        <a href="https://theskillgenie.com" style={{ 
          padding: '10px 20px', 
          background: 'var(--primary-blue, #4F46E5)', 
          color: 'white', 
          borderRadius: '5px',
          textDecoration: 'none'
        }}>
          Go to TheSkillGenie Home
        </a>
      </div>
    );
  }

  if (error === 'TENANT_INACTIVE') {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '10px' }}>Account Inactive</h1>
        <p style={{ color: '#64748b', marginBottom: '20px' }}>
          The account for <strong>{window.location.hostname}</strong> is currently suspended or inactive.
        </p>
        <p>Please contact your institution administrator.</p>
      </div>
    );
  }

  if (error === 'RESOLUTION_FAILED') {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '10px' }}>System Unavailable</h1>
        <p style={{ color: '#64748b', marginBottom: '20px' }}>
          We are having trouble connecting to the platform services.
        </p>
        <button 
          onClick={() => window.location.reload()}
          style={{ 
            padding: '10px 20px', 
            background: 'var(--primary-blue, #4F46E5)', 
            color: 'white', 
            borderRadius: '5px',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export default TenantContext;

